import { isNull } from "lodash";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

// Imported actions
import { resetDatabase } from "../../actions/admin.js";

// Imported assets
import {
  LockIcon,
  LogoutIcon,
  ProfileIcon,
  SettingsIcon,
} from "../../assets/icons/svgs/Icons.jsx";
import defaultAvatar from "../../assets/icons/ProfilePicForNavBar.svg";
import StrataLogo from "../../assets/images/STRATALogo_Black.png";

// TODO: Remove temp avatar for demos
import lgAvatar from "../../assets/temp/lg-demo-avatar.png";
import tonyWaltersAvatar from "../../assets/temp/tony-k-walters.png";

// Imported components
import ResetDatabaseDialog from "../ResetDatabaseDialog.tsx";

// Imported contexts
import { useUserContext } from "../../contexts/User.tsx";

// Styled components
type ContainerProps = {
  $isHidden?: boolean;
};

type NavLink = {
  label: string;
  redirecturl?: string;
  subpages?: { label: string; redirecturl: string }[];
  reqroles?: string[];
};

const Avatar = styled.img`
  border-radius: 50%;
  cursor: pointer;
  height: auto;
  transition: all 0.3s ease-in-out;
  width: clamp(24px, 3.8vw, 40px);
`;

const AvatarContainer = styled.div`
  align-items: center;
  display: flex;
  gap: clamp(3px, 0.8vw, 10px);
  min-width: clamp(40px, 4vw, 70px);
  overflow: visible;
`;

const Container = styled.nav<ContainerProps>`
  align-items: center;
  background: white;
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.12);
  display: ${(props) => (props.$isHidden ? "none" : "flex")};
  height: auto;
  justify-content: space-between;
  left: 0;
  min-width: 280px;
  overflow: visible;
  padding: clamp(4px, 1.5vw, 16px);
  position: fixed;
  top: 0;
  transition: all 0.3s ease-in-out;
  width: 100%;
  z-index: 100;
`;

const Link = styled.div`
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  font-size: clamp(10px, 1.8vw, 18px);
  justify-content: center;
  min-width: unset;
  padding: clamp(2px, 0.6vw, 6px) clamp(3px, 0.7vw, 10px);
  transition: all 0.3s ease-in-out;
  white-space: nowrap;
  width: auto;

  &:hover {
    background: var(--terbine-blue);
    color: white;
  }
`;

const Links = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: clamp(3px, 0.8vw, 15px);
  transition: all 0.3s ease-in-out;
`;

const Logo = styled.img`
  cursor: pointer;
  height: clamp(28px, 4.5vw, 48px);
  margin-right: clamp(2px, 1vw, 12px);
  transition: all 0.3s ease-in-out;
`;

const SubPagesNavContainer = styled.div`
  left: 95%;
  position: absolute;
  top: 50%;
  z-index: 11;
`;

const UserContainer = styled.div`
  align-items: center;
  background: #f2f2f2;
  border-radius: 8px;
  color: black;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  font-size: clamp(10px, 1.3vw, 14px);
  gap: clamp(5px, 1vw, 20px);
  max-width: clamp(50px, 13vw, 180px);
  overflow: hidden;
  padding: clamp(1px, 0.4vw, 5px) clamp(3px, 0.7vw, 8px);
  text-overflow: ellipsis;
  transition: all 0.3s ease-in-out;
  white-space: nowrap;
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
`;

const Icon = styled.img`
  height: auto;
  margin-right: 6px;
  width: 14px;
`;

const SubMenuContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #d1d5db;
  border-radius: 5px;
  z-index: 10;
  min-width: 170px;
`;

const SubMenuWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #d1d5db;
  border-radius: 5px;
  z-index: 20;
  min-width: 180px;
  padding: 4px 0;
`;

const SubMenuItem = styled.div`
  padding: 8px 16px;
  font-family: "Roboto-Regular", sans-serif;
  font-size: 14px;
  cursor: pointer;
  color: #000000;
  white-space: nowrap;
  transition: background 0.2s ease;

  &:hover {
    background: #3e87dc;
    color: white;
  }
`;

const UserInfoText = styled.span`
  font-size: clamp(10px, 1.3vw, 14px);
`;

const navLinks: NavLink[] = [
  {
    label: "Mission Control",
    subpages: [
      { label: "Main Display", redirecturl: "/main-display" },
      { label: "Console Display", redirecturl: "/console-view" },
      { label: "Landing Page", redirecturl: "/dashboard" },
      { label: "Alerts", redirecturl: "/view-alerts" },
      { label: "Notifications", redirecturl: "/notification-configurations/manage" }
    ]
  },
  {
    label: "Configure",
    subpages: [
      { label: "Configured Metadata", redirecturl: "/configured-metadata" },
      { label: "Policy Engine", redirecturl: "/policies" },
      { label: "Reporting Data", redirecturl: "/data-analytics" },
      { label: "Stations/Equipment", redirecturl: "/stations-equipment" }
    ]
  },
  {
    label: "Security",
    redirecturl: "/security"
  },
  {
    label: "Technical",
    redirecturl: "/technical"
  },
  {
    label: "Accounts",
    subpages: [
      { label: "Create / Manage Accounts", redirecturl: "/accounts/create-and-manage" },
      { label: "Payment Processing", redirecturl: "/accounts/payment-processing" },
    ]
  }
];

const userLinks = [
  {
    icon: "Profile",
    label: "Profile Information",
    redirecturl: "/profile/information",
    reqroles: null,
    type: "link",
  },
  {
    icon: "Lock",
    label: "Security Settings",
    redirecturl: "/profile/security",
    reqroles: null,
    type: "link",
  },
  {
    icon: "Settings",
    label: "Administrator Settings",
    redirecturl: "/profile/administrator-settings",
    reqroles: ["Administrator", "Super Administrator"],
    type: "link",
  },
  {
    icon: "Logout",
    label: "Logout",
    redirecturl: null,
    reqroles: null,
    type: "logout",
  },
];

const NavigationBar = ({ isHidden = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { user, userLogout } = useUserContext();

  const [subMenuDisplayed, setSubMenuDisplayed] = useState(null);

  const [isResetDatabaseDialogOpen, setIsResetDatabaseDialogOpen] =
    useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleKeyDown = (e, redirecturl) => {
    switch (e.key) {
      case "Enter": {
        navigate(redirecturl);
        break;
      }

      default: {
        break;
      }
    }
  };

  const handleLogout = async (e) => {
    await userLogout().then(() => {
      navigate("/login");
    });
  };

  const handleReset = async (e) => {
    resetDatabase();
    toggleResetDatabaseDialog();
  };

  const toggleResetDatabaseDialog = () => {
    setIsResetDatabaseDialogOpen(!isResetDatabaseDialogOpen);
  };

  const handleUserItemClick = (e, option) => {
    e?.preventDefault();

    if (option.type === "link") {
      navigate(option.redirecturl);
    } else if (option.type === "logout") {
      handleLogout(e);
    }
  };

  const handleMenuParentClick = (e) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleUserMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Container $isHidden={isHidden}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "clamp(5px, 2vw, 40px)",
        }}
      >
        <Logo
          alt="Terbine Logo"
          aria-label="Terbine Logo"
          onClick={() => navigate("/summary")}
          src={StrataLogo}
        />
        <Links>
          {navLinks.map((link, i) => {
            let subnav = null;
            if (link.subpages) {
              subnav = (
                <SubMenuWrapper>
                  {link.subpages.map((page, i) => (
                    <SubMenuItem
                      key={`submenu-${link.label}-${i}`}
                      onClick={() => {
                        setSubMenuDisplayed(null);
                        navigate(page.redirecturl);
                      }}
                    >
                      {page.label}
                    </SubMenuItem>
                  ))}
                </SubMenuWrapper>
              );
            }

            let hasReqRole;
            if (link.reqroles) {
              hasReqRole = false;
              let userRoles = user.roles.map((role) => role.type.codeName);
              for (let i = 0; i < link.reqroles.length; i++) {
                hasReqRole = hasReqRole || userRoles.includes(link.reqroles[i]);
                if (hasReqRole) break;
              }
            } else hasReqRole = true;

            return (
              hasReqRole && (
                <div
                  key={`top-nav-bar-link-${i}`}
                  style={{ position: "relative", display: "inline-block" }}
                >
                  <div
                    key={`top-nav-bar-link-${i}`}
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <Link
                      onClick={() =>
                        link.redirecturl
                          ? navigate(link.redirecturl)
                          : setSubMenuDisplayed(
                              subMenuDisplayed === link.label ? null : link.label
                            )
                      }
                      tabIndex={0}
                    >
                      {link.label}
                    </Link>

                    {subMenuDisplayed === link.label && subnav}
                  </div>
                </div>
              )
            );
          })}
        </Links>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "clamp(5px, 2vw, 40px)",
        }}
      >
        <UserContainer
          onClick={() => {
            setSubMenuDisplayed(subMenuDisplayed === "user" ? null : "user");
          }}
        >
          <UserInfoContainer>
            <UserInfoText>{user.displayName}</UserInfoText>
            <UserInfoText>
              {user.roles.map((role) => role.type.codeName).join(" / ")}
            </UserInfoText>
          </UserInfoContainer>
          <AvatarContainer>
            <Avatar
              // onClick={handleMenuParentClick}
              src={
                user.userId === "joseph.d.scott@lge.com"
                  ? lgAvatar
                  : user.userId === "tony.k.walters@cnmp.com"
                  ? tonyWaltersAvatar
                  : defaultAvatar
              }
            />
          </AvatarContainer>
          {subMenuDisplayed === "user" && (
            <SubPagesNavContainer
              style={{ transform: "translateX(-80%) translateY(10%)" }}
            >
              <Box
                sx={{
                  bgcolor: "background.paper",
                  maxWidth: 360,
                  minWidth: 200,
                  width: "100%",
                }}
              >
                <nav>
                  <List>
                    {userLinks
                      .filter((link) => {
                        if (isNull(link.reqroles)) return true;

                        let hasReqRole = false;
                        let userRoles = user.roles.map(
                          (role) => role.type.codeName
                        );
                        for (let i = 0; i < link.reqroles.length; i++) {
                          hasReqRole =
                            hasReqRole || userRoles.includes(link.reqroles[i]);
                          if (hasReqRole) break;
                        }

                        return hasReqRole;
                      })
                      .map((link, i) => (
                        <ListItem
                          disablePadding
                          key={`user-link-${i}`}
                          sx={{ "&:hover > div > svg": { fill: "white" } }}
                        >
                          <ListItemButton
                            onClick={(e) => handleUserItemClick(e, link)}
                          >
                            {link.icon === "Profile" ? (
                              <ProfileIcon />
                            ) : link.icon === "Lock" ? (
                              <LockIcon />
                            ) : link.icon === "Logout" ? (
                              <LogoutIcon />
                            ) : link.icon === "Settings" ? (
                              <SettingsIcon />
                            ) : null}
                            <ListItemText primary={link.label} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    {user.roles
                      .map((role) => role.type.codeName)
                      .includes("Administrator") && (
                      <ListItem disablePadding>
                        <ListItemButton onClick={toggleResetDatabaseDialog}>
                          <ListItemText primary={"Reset Status"} />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </nav>
              </Box>
            </SubPagesNavContainer>
          )}
        </UserContainer>
      </div>

      <ResetDatabaseDialog
        handleReset={handleReset}
        isModalOpen={isResetDatabaseDialogOpen}
        toggleModal={toggleResetDatabaseDialog}
      />
    </Container>
  );
};

export default NavigationBar;
